import type { SupabaseClient } from '@supabase/supabase-js';
import type { CreditoCrearConfig } from '../../components/facturas/types';
import {
  fechasPlanCuotas,
  frecuenciaDiasToDb,
  montoPagadoPorCuota,
  partesIgualesCOP,
  estadoCuota,
} from './cuotasPlan';

function numDb(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

/** Fecha local YYYY-MM-DD a partir de hoy + días. */
function fechaLocalIsoMasDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Si `raw` no es YYYY-MM-DD válido, usa hoy + `fallbackDias`. */
function normalizarFechaIsoYyyyMmDd(raw: string | undefined | null, fallbackDias: number): string {
  const s = String(raw ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return fechaLocalIsoMasDias(fallbackDias);
}

function estadoCreditoDesdeMontos(montoTotal: number, montoPagado: number): 'activo' | 'pagado' {
  return montoPagado >= montoTotal && montoTotal > 0 ? 'pagado' : 'activo';
}

/** Inserta filas en `cuotas` para un `creditos.id` existente (tras DELETE o en alta nueva). */
async function insertarSoloCuotas(
  supabase: SupabaseClient,
  {
    creditoId,
    empresaId,
    totalC,
    paidC,
    config,
  }: {
    creditoId: string;
    empresaId: string;
    totalC: number;
    paidC: number;
    config: CreditoCrearConfig;
  }
): Promise<void> {
  try {
    if (config.modo === 'simple') {
      const fv = normalizarFechaIsoYyyyMmDd(config.fechaVencimientoSimple, 30);
      const cEst =
        paidC <= 0 ? 'pendiente' : paidC >= totalC ? 'pagada' : 'parcial';
      const { error: cuotaErr } = await supabase.from('cuotas').insert({
        credito_id: creditoId,
        empresa_id: empresaId,
        numero_cuota: 1,
        monto_cuota: totalC,
        monto_pagado: paidC,
        fecha_vencimiento: fv,
        estado: cEst,
      });
      if (cuotaErr) throw cuotaErr;
      return;
    }

    const n = Math.max(2, Math.min(48, Math.floor(Number(config.numCuotas) || 0)));
    const frec = (config.frecuenciaDias ?? 30) as 7 | 15 | 30;
    const primera = normalizarFechaIsoYyyyMmDd(config.primeraFechaVencimiento, 30);
    if (n < 2) return;

    const montos = partesIgualesCOP(totalC, n);
    const pagadoPor = montoPagadoPorCuota(montos, paidC);
    const fechas = fechasPlanCuotas(primera, n, frec);
    const rows = montos.map((monto, i) => {
      const mp = pagadoPor[i] ?? 0;
      return {
        credito_id: creditoId,
        empresa_id: empresaId,
        numero_cuota: i + 1,
        monto_cuota: monto,
        monto_pagado: mp,
        fecha_vencimiento: fechas[i] ?? primera,
        estado: estadoCuota(monto, mp),
      };
    });
    const { error: cuotaErr } = await supabase.from('cuotas').insert(rows);
    if (cuotaErr) throw cuotaErr;
  } catch (err) {
    console.error('=== insertarSoloCuotas FAILED ===', err);
    throw err;
  }
}

/**
 * Tras UPDATE en `facturas` con `metodo_pago` CREDITO: crea o alinea `creditos` + `cuotas` como en alta.
 * Si ya hay abonos en cuotas, solo actualiza montos en `creditos` (no rehace el plan).
 */
export async function sincronizarCreditoTrasUpdateFactura(
  supabase: SupabaseClient,
  {
    facturaId,
    empresaId,
    clienteId,
    metodoPago,
    total,
    montoPagado,
    creditoCrearConfig,
    vencimientoIso,
  }: {
    facturaId: string;
    empresaId: string;
    clienteId: string | null | undefined;
    metodoPago: string | null | undefined;
    total: number;
    montoPagado: number;
    creditoCrearConfig?: CreditoCrearConfig;
    vencimientoIso: string | null | undefined;
  }
): Promise<void> {
  const metodoCreacion = String(metodoPago ?? '').trim().toUpperCase();
  if (metodoCreacion !== 'CREDITO' || !clienteId) return;

  const totalC = numDb(total);
  const paidC = numDb(montoPagado);
  if (totalC <= 0) return;
  const pendienteCreacion = Math.max(0, totalC - paidC);
  if (pendienteCreacion <= 0) return;

  const vencRaw = vencimientoIso;
  const fvFallback =
    vencRaw && !Number.isNaN(new Date(vencRaw).getTime())
      ? new Date(vencRaw).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  const config: CreditoCrearConfig =
    creditoCrearConfig ?? { modo: 'simple', fechaVencimientoSimple: fvFallback };

  const { data: existing } = await supabase
    .from('creditos')
    .select('id')
    .eq('factura_id', facturaId)
    .maybeSingle();

  if (!existing?.id) {
    await crearCreditoTrasFactura(supabase, {
      empresaId,
      facturaId,
      clienteId: String(clienteId),
      total: totalC,
      montoPagado: paidC,
      config,
    });
    return;
  }

  const creditoId = existing.id;
  const { data: filasCuota } = await supabase
    .from('cuotas')
    .select('monto_pagado')
    .eq('credito_id', creditoId);
  const hasPagos = (filasCuota ?? []).some((c) => numDb(c.monto_pagado) > 0);

  if (hasPagos) {
    const { error: upErr } = await supabase
      .from('creditos')
      .update({
        monto_total: totalC,
        monto_pagado: paidC,
        estado: estadoCreditoDesdeMontos(totalC, paidC),
        updated_at: new Date().toISOString(),
      })
      .eq('id', creditoId);
    if (upErr) throw upErr;
    return;
  }

  if (config.modo === 'simple') {
    const fv = normalizarFechaIsoYyyyMmDd(config.fechaVencimientoSimple, 30);
    const { error: upCred } = await supabase
      .from('creditos')
      .update({
        tipo: 'simple',
        monto_total: totalC,
        monto_pagado: paidC,
        fecha_vencimiento: fv,
        numero_cuotas: 1,
        frecuencia: 'unica',
        fecha_primera_cuota: fv,
        monto_cuota: totalC,
        estado: estadoCreditoDesdeMontos(totalC, paidC),
        updated_at: new Date().toISOString(),
      })
      .eq('id', creditoId);
    if (upCred) throw upCred;
  } else {
    const n = Math.max(2, Math.min(48, Math.floor(Number(config.numCuotas) || 0)));
    const frec = (config.frecuenciaDias ?? 30) as 7 | 15 | 30;
    const primera = normalizarFechaIsoYyyyMmDd(config.primeraFechaVencimiento, 30);
    if (n < 2) return;
    const montos = partesIgualesCOP(totalC, n);
    const fechas = fechasPlanCuotas(primera, n, frec);
    const ultima = fechas[fechas.length - 1] ?? primera;
    const frecuenciaText = frecuenciaDiasToDb(frec);
    const montoCuotaRef = montos[0] ?? 0;
    const { error: upCred } = await supabase
      .from('creditos')
      .update({
        tipo: 'plan_pagos',
        monto_total: totalC,
        monto_pagado: paidC,
        fecha_vencimiento: ultima,
        numero_cuotas: n,
        frecuencia: frecuenciaText,
        fecha_primera_cuota: primera,
        monto_cuota: montoCuotaRef,
        estado: estadoCreditoDesdeMontos(totalC, paidC),
        updated_at: new Date().toISOString(),
      })
      .eq('id', creditoId);
    if (upCred) throw upCred;
  }

  const { error: delErr } = await supabase.from('cuotas').delete().eq('credito_id', creditoId);
  if (delErr) throw delErr;
  await insertarSoloCuotas(supabase, {
    creditoId,
    empresaId,
    totalC,
    paidC,
    config,
  });
}

/**
 * Tras INSERT en `facturas` con `metodo_pago` CREDITO: crea `creditos` (incluye columnas de plan
 * `numero_cuotas`, `frecuencia`, `fecha_primera_cuota`, `monto_cuota`) y filas en `cuotas`
 * según `creditoCrearConfig` del wizard (independiente de la fecha de entrega).
 */
export async function crearCreditoTrasFactura(
  supabase: SupabaseClient,
  {
    empresaId,
    facturaId,
    clienteId,
    total,
    montoPagado,
    config,
  }: {
    empresaId: string;
    facturaId: string;
    clienteId: string;
    total: number;
    montoPagado: number;
    config: CreditoCrearConfig;
  }
): Promise<void> {
  console.log('=== crearCreditoTrasFactura FIRST LINE ===', {
    empresaId,
    facturaId,
    clienteId,
    total,
    montoPagado,
    config,
  });
  try {
    const totalC = numDb(total);
    const paidC = numDb(montoPagado);
    if (totalC <= 0) return;
    const pend = Math.max(0, totalC - paidC);
    if (pend <= 0) return;

    const { data: yaExiste } = await supabase
      .from('creditos')
      .select('id')
      .eq('factura_id', facturaId)
      .maybeSingle();
    if (yaExiste) return;

    if (config.modo === 'simple') {
      const fv = normalizarFechaIsoYyyyMmDd(config.fechaVencimientoSimple, 30);

      console.log('=== crearCreditoTrasFactura before creditos INSERT ===', {
        branch: 'simple',
        insertPayload: {
          tipo: 'simple',
          monto_total: totalC,
          monto_pagado: paidC,
          fecha_vencimiento: fv,
        },
      });
      const { data: nuevoCredito, error: credErr } = await supabase
        .from('creditos')
        .insert({
          empresa_id: empresaId,
          factura_id: facturaId,
          cliente_id: clienteId,
          tipo: 'simple',
          monto_total: totalC,
          monto_pagado: paidC,
          fecha_vencimiento: fv,
          numero_cuotas: 1,
          frecuencia: 'unica',
          fecha_primera_cuota: fv,
          monto_cuota: totalC,
          estado: estadoCreditoDesdeMontos(totalC, paidC),
        })
        .select('id')
        .single();
      if (credErr) throw credErr;
      if (!nuevoCredito?.id) return;

      await insertarSoloCuotas(supabase, {
        creditoId: nuevoCredito.id,
        empresaId,
        totalC,
        paidC,
        config,
      });
      return;
    }

    // plan_pagos
    const n = Math.max(2, Math.min(48, Math.floor(Number(config.numCuotas) || 0)));
    const frec = (config.frecuenciaDias ?? 30) as 7 | 15 | 30;
    const primera = normalizarFechaIsoYyyyMmDd(config.primeraFechaVencimiento, 30);
    if (n < 2) return;

    const montos = partesIgualesCOP(totalC, n);
    const fechas = fechasPlanCuotas(primera, n, frec);
    const ultima = fechas[fechas.length - 1] ?? primera;
    const frecuenciaText = frecuenciaDiasToDb(frec);
    const montoCuotaRef = montos[0] ?? 0;

    console.log('=== crearCreditoTrasFactura before creditos INSERT ===', {
      branch: 'plan_pagos',
      n,
      primera,
      frec,
      insertPayload: {
        tipo: 'plan_pagos',
        monto_total: totalC,
        numero_cuotas: n,
        frecuencia: frecuenciaText,
        fecha_primera_cuota: primera,
        fecha_vencimiento: ultima,
        monto_cuota: montoCuotaRef,
      },
    });
    const { data: nuevoCredito, error: credErr } = await supabase
      .from('creditos')
      .insert({
        empresa_id: empresaId,
        factura_id: facturaId,
        cliente_id: clienteId,
        tipo: 'plan_pagos',
        monto_total: totalC,
        monto_pagado: paidC,
        fecha_vencimiento: ultima,
        numero_cuotas: n,
        frecuencia: frecuenciaText,
        fecha_primera_cuota: primera,
        monto_cuota: montoCuotaRef,
        estado: estadoCreditoDesdeMontos(totalC, paidC),
      })
      .select('id')
      .single();
    if (credErr) throw credErr;
    if (!nuevoCredito?.id) return;

    await insertarSoloCuotas(supabase, {
      creditoId: nuevoCredito.id,
      empresaId,
      totalC,
      paidC,
      config,
    });
  } catch (err) {
    console.error('=== crearCreditoTrasFactura FAILED (catch) ===', err);
    throw err;
  }
}
